import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('investments');
export interface InvestmentsInfoRec {
  id: string;
  userId: string;
  financialInstitution?: string;
  accountType?: string;
  investmentType?: string;
  accountValue?: number;
  onlineAccountUrl?: string;
  customerServicePhone?: string;
  supportEmail?: string;
  beneficiaryInfo?: string;
  notes?: string;
  isLocked?: boolean;
}

class InvestmentsInfoService {
  async getAllInvestmentsInfo(targetUserId?: string): Promise<InvestmentsInfoRec[]> {
    const params = targetUserId ? `?targetUserId=${targetUserId}` : '';
    const response = await http.get(`/api/investments-info${params}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch investments information');
    }

    return response.data.data || [];
  }

  async getInvestmentsInfo(): Promise<InvestmentsInfoRec[]> {
    return this.getAllInvestmentsInfo();
  }

  async createInvestmentsInfo(data: Partial<InvestmentsInfoRec>): Promise<InvestmentsInfoRec> {
    const response = await http.post('/api/investments-info', data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save investments information');
    }

    return response.data.data;
  }

  async updateInvestmentsInfo(id: string, data: Partial<InvestmentsInfoRec>): Promise<InvestmentsInfoRec> {
    const response = await http.patch(`/api/investments-info/${id}`, data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update investments information');
    }

    return response.data.data;
  }

  async deleteInvestmentsInfo(id: string): Promise<void> {
    const response = await http.delete(`/api/investments-info/${id}`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete investments information');
    }
  }

  async lockInvestmentsInfo(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for investments ID:', id);
    const response = await http.post(`/api/investments-info/${id}/lock`);
    logger.debug('[Service Debug] Lock API response:', response.data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock investments information');
    }
  }

  async unlockInvestmentsInfo(id: string, granteeEmails: string[]): Promise<void> {
    const response = await http.post(`/api/investments-info/${id}/unlock`, { granteeEmails });
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock investments information');
    }
  }

  async getInvestmentsAccessGrants(id: string): Promise<any[]> {
    const response = await http.get(`/api/investments-info/${id}/access-grants`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch access grants');
    }

    return response.data.data || [];
  }
}

export const investmentsInfoService = new InvestmentsInfoService();